import { inject, Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { ID } from '@core/ngrx-legacy';
import { TellentHttpApiService } from '@core/tellent-http-legacy';
import {
  AssignMembersOptions,
  GeneratePositionDescriptionPayload,
  GeneratePositionDescriptionResponse,
  TellentPosition,
  TellentPositionResponse,
  UpdatePositionPayload,
} from '@core/tellent-positions-types';
import { ORG_GUID } from '@core/tellent-types';

@Injectable({
  providedIn: 'root',
})
export class TellentPositionsHttpClient {
  private readonly httpApi = inject(TellentHttpApiService);
  private readonly orgGuid = inject(ORG_GUID);

  public fetchPositions(): Observable<TellentPosition[]> {
    return this.httpApi
      .get(`/positions/org/${this.orgGuid}/positions`, null, { skipCompany: true })
      .pipe(map(response => response.data));
  }

  public createPosition(name: string): Observable<TellentPosition> {
    return this.httpApi
      .post(`/positions/org/${this.orgGuid}/positions`, null, { name }, { skipCompany: true })
      .pipe(map(response => response.position));
  }

  public fetchPosition(positionGuid: ID | string): Observable<TellentPositionResponse> {
    return this.httpApi
      .get(`/positions/org/${this.orgGuid}/positions/${positionGuid}`, null, { skipCompany: true })
      .pipe(map(response => ({ data: response.position })));
  }

  public updatePosition(
    positionGuid: string,
    position: UpdatePositionPayload,
  ): Observable<TellentPosition> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/positions/${positionGuid}`,
        null,
        { ...position },
        { skipCompany: true },
      )
      .pipe(map(response => response.position));
  }

  public assignMembers(
    positionGuid: string,
    membersGuids: string[],
    options?: AssignMembersOptions,
  ): Observable<TellentPosition> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/positions/${positionGuid}/assign`,
        null,
        { membersGuids, ...options },
        { skipCompany: true },
      )
      .pipe(map(response => response.position));
  }

  // No `startDate`: a correction only restates which position the member holds, and the dialog
  // never asks for a date, so the backend decides it.
  public reassignMembers(
    positionGuid: string,
    membersGuids: string[],
  ): Observable<TellentPosition> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/positions/${positionGuid}/reassign`,
        null,
        { membersGuids },
        { skipCompany: true },
      )
      .pipe(map(response => response.position));
  }

  public unassignMembers(
    positionGuid: string,
    membersGuids: string[],
  ): Observable<TellentPosition> {
    return this.httpApi
      .patch(
        `/positions/org/${this.orgGuid}/positions/${positionGuid}/unassign`,
        null,
        { membersGuids },
        { skipCompany: true },
      )
      .pipe(map(response => response.position));
  }

  public generateDescription(
    payload: GeneratePositionDescriptionPayload,
  ): Observable<GeneratePositionDescriptionResponse> {
    return this.httpApi.post(
      `/positions/org/${this.orgGuid}/positions/description`,
      null,
      payload,
      {
        skipCompany: true,
      },
    );
  }

  public deletePosition(positionGuid: string): Observable<void> {
    return this.httpApi
      .delete(`/positions/org/${this.orgGuid}/positions/${positionGuid}`, null, {
        skipCompany: true,
      })
      .pipe(map(() => undefined));
  }
}
